import { useEffect, useState } from "react";
import { X, Smartphone, Share } from "lucide-react";

const DISMISS_KEY = "mm-install-dismissed";

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    if (isStandalone() || hidden) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    if (isIos()) {
      const t = setTimeout(() => setShowIos(true), 4000);
      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
        clearTimeout(t);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [hidden]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setHidden(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    dismiss();
  };

  if (hidden || (!deferred && !showIos)) return null;

  return (
    <div
      data-testid="install-app-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[90] rounded-xl border border-blue-500/30 bg-[#0a0f1a]/95 backdrop-blur-md p-4 shadow-2xl shadow-blue-900/30"
    >
      <button
        data-testid="install-banner-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 text-neutral-500 hover:text-white transition-colors"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
          <Smartphone size={20} className="text-blue-400" />
        </div>
        <div className="pr-4">
          <p className="text-sm font-semibold text-white">Get the Mason's Mobile app</p>
          {deferred ? (
            <>
              <p className="text-xs text-neutral-400 mt-0.5">Book service and track jobs right from your home screen.</p>
              <button
                data-testid="install-app-button"
                onClick={install}
                className="mt-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-4 py-2 transition-colors"
              >
                Install App
              </button>
            </>
          ) : (
            <p className="text-xs text-neutral-400 mt-0.5 leading-relaxed">
              Tap <Share size={12} className="inline -mt-0.5 text-blue-400" /> <span className="text-neutral-300">Share</span>, then{" "}
              <span className="text-neutral-300">"Add to Home Screen"</span> to install.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
